import React, { useState } from 'react';
import SEO from '../../components/ui/SEO';
import { AlertTriangle, CircleCheck, LockKeyhole, MessagesSquare, Zap } from 'lucide-react';

const blankForm = { name: '', company: '', phone: '', email: '', productType: '', quantity: '', message: '' };

export default function OemPage() {
  const [form,      setForm]      = useState(blankForm);
  const [submitted, setSubmitted] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/oem', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact_name: form.name.trim(),
          company:      form.company.trim() || null,
          phone:        form.phone.trim(),
          email:        form.email.trim(),
          product_type: form.productType || null,
          quantity:     form.quantity || null,
          message:      form.message.trim() || null,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error ?? '접수 중 오류가 발생했습니다. 다시 시도해 주세요.');
        setLoading(false);
        return;
      }

      setSubmitted(true);
    } catch {
      setError('서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  };

  /* ── 접수 완료 화면 ── */
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
        <div className="text-center p-10 bg-white rounded-2xl shadow-xl max-w-md w-full">
          <CircleCheck size={52} className="mx-auto mb-4 text-emerald-600" aria-hidden="true" />
          <h2 className="text-2xl font-bold text-stone-900 mb-2">전문 제작 문의가 접수되었습니다</h2>
          <p className="text-stone-500 mb-2">전문 담당자가 1~2 영업일 내에 연락드리겠습니다.</p>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 text-left mt-4 mb-6">
            <p className="font-semibold mb-1">접수 정보</p>
            <p>담당자: <strong>{form.name}</strong></p>
            {form.company && <p>회사명: <strong>{form.company}</strong></p>}
            <p>연락처: <strong>{form.phone}</strong></p>
            <p>이메일: <strong>{form.email}</strong></p>
          </div>
          <button
            onClick={() => { setSubmitted(false); setForm(blankForm); }}
            className="border border-stone-300 px-6 py-2 rounded-lg hover:bg-stone-50 transition text-sm">
            다시 문의하기
          </button>
        </div>
      </div>
    );
  }

  /* ── 문의 폼 ── */
  return (
    <div className="min-h-screen bg-stone-50 py-16 px-4">
      <SEO
        title="OEM/ODM 전문 제작 문의"
        path="/oem"
        description="COBRAND OEM/ODM 전문 가방 제작 문의. 소량 샘플부터 대량 생산까지. 핸드백, 백팩, 크로스백, 여행가방 맞춤 제작 상담."
        keywords="OEM가방,ODM가방,가방맞춤제작,가방공장,소량제작,핸드백OEM,백팩ODM"
      />
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-amber-500 text-xs tracking-widest uppercase mb-2">전문 OEM / ODM</p>
          <h1 className="text-4xl font-bold text-stone-900 mb-3">전문 제작 문의</h1>
          <p className="text-stone-500">소량 샘플부터 대량 생산까지, 전문 파트너와 함께 최적의 가방을 전문 제작합니다.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-5">

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">담당자명 *</label>
                <input type="text" required value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="홍길동" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">회사명</label>
                <input type="text" value={form.company}
                  onChange={e => setForm({...form, company: e.target.value})}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="(주)회사명" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">연락처 *</label>
                <input type="tel" required value={form.phone}
                  onChange={e => setForm({...form, phone: e.target.value})}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="010-0000-0000" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">이메일 *</label>
                <input type="email" required value={form.email}
                  onChange={e => setForm({...form, email: e.target.value})}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="email@company.com" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">제품 유형 *</label>
                <select required value={form.productType}
                  onChange={e => setForm({...form, productType: e.target.value})}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                  <option value="">-- 선택 --</option>
                  <option>핸드백 / 토트백</option>
                  <option>숄더백 / 크로스백</option>
                  <option>백팩</option>
                  <option>여행용 가방</option>
                  <option>기타</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">예상 수량 *</label>
                <select required value={form.quantity}
                  onChange={e => setForm({...form, quantity: e.target.value})}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                  <option value="">-- 선택 --</option>
                  <option>샘플 (1~10개)</option>
                  <option>소량 (10~100개)</option>
                  <option>중량 (100~500개)</option>
                  <option>대량 (500개 이상)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">상세 요청사항</label>
              <textarea value={form.message}
                onChange={e => setForm({...form, message: e.target.value})}
                className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                rows={4}
                placeholder="원하시는 소재, 색상, 디자인, 납기 희망일 등을 자유롭게 입력해주세요." />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-xl flex items-center gap-2">
                <AlertTriangle size={17} aria-hidden="true" /> {error}
              </div>
            )}

            <button type="submit" disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold py-3.5 rounded-xl transition text-sm flex items-center justify-center gap-2">
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  접수 중...
                </>
              ) : '문의 접수하기'}
            </button>
          </form>
        </div>

        {/* 안내 문구 */}
        <div className="mt-6 grid grid-cols-3 gap-4 text-center">
          {[
            { icon: Zap, text: '1~2 영업일 내 답변' },
            { icon: LockKeyhole, text: '개인정보 안전 보호' },
            { icon: MessagesSquare, text: '전문 담당자 상담' },
          ].map((item, i) => (
            <div key={i} className="bg-white rounded-xl p-3 shadow-sm">
              <item.icon size={23} className="mx-auto mb-2 text-stone-700" aria-hidden="true" />
              <p className="text-xs text-stone-500">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
